/**
 * Stable paragraph/block identity for TipTap chapter docs.
 * Attribute name is `attrs.id` (HTML `data-paragraph-id`); plan "blockId" maps to this.
 */

export const PARAGRAPH_BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'codeBlock',
  'image',
]);

export type ChapterBlockNode = {
  type: string;
  attrs?: { id?: string | null; level?: number | null; [key: string]: unknown };
  content?: unknown[];
};

export type ChapterDocContent = {
  type: 'doc';
  content?: ChapterBlockNode[];
};

export type ChapterParagraphRef = {
  /** Zero-based index among tracked block types in document order. */
  index: number;
  /** Stable block id (`data-paragraph-id` in HTML). */
  id: string;
  type: string;
  text: string;
  level?: number;
  /** Full TipTap node (for replace/diff). */
  node: ChapterBlockNode;
};

function createParagraphId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function tipTapInlineText(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  const record = value as Record<string, unknown>;
  if (record.type === 'text' && typeof record.text === 'string') return record.text;
  if (record.type === 'hardBreak') return '\n';
  const children = Array.isArray(record.content) ? record.content : [];
  return children.map((child) => tipTapInlineText(child)).join('');
}

function blockPlainText(node: ChapterBlockNode): string {
  if (node.type === 'image') {
    const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
    return alt.trim();
  }
  if (node.type === 'blockquote' || node.type === 'codeBlock') {
    const children = Array.isArray(node.content) ? node.content : [];
    return children
      .map((child) => {
        if (child && typeof child === 'object' && PARAGRAPH_BLOCK_TYPES.has((child as ChapterBlockNode).type)) {
          return blockPlainText(child as ChapterBlockNode);
        }
        return tipTapInlineText(child);
      })
      .map((t) => t.trim())
      .filter(Boolean)
      .join('\n');
  }
  return tipTapInlineText(node).trim();
}

function isParagraphBlock(node: unknown): node is ChapterBlockNode {
  return Boolean(node && typeof node === 'object' && PARAGRAPH_BLOCK_TYPES.has((node as ChapterBlockNode).type));
}

/** Assign ids to tracked blocks that are missing them. Idempotent. */
export function ensureParagraphIds(doc: ChapterDocContent): ChapterDocContent {
  const content = Array.isArray(doc.content) ? doc.content : [];
  return {
    type: 'doc',
    content: content.map((node) => {
      if (!isParagraphBlock(node)) return node;
      const attrs = { ...(node.attrs ?? {}) };
      if (!attrs.id || typeof attrs.id !== 'string' || !attrs.id.trim()) {
        attrs.id = createParagraphId();
      }
      return { ...node, attrs };
    }),
  };
}

/** Alias used by version-diff / accept-reject plans. */
export const ensureBlockIds = ensureParagraphIds;

/**
 * Remap paragraph ids on `side` so they align with `base` for three-way merge.
 *
 * Calling `ensureParagraphIds` independently on base/mine/theirs mints divergent
 * UUIDs for the same paragraphs, which the id-keyed merge then treats as inserts
 * on both sides (concatenating full chapter copies). This aligns by:
 * 1) shared existing ids, 2) exact plain-text match, 3) remaining order, 4) fresh
 * ids only for true inserts.
 */
export function alignParagraphIdsToBase(
  baseDoc: ChapterDocContent,
  sideDoc: ChapterDocContent
): ChapterDocContent {
  const base = ensureParagraphIds(baseDoc);
  const baseParas = listChapterParagraphs(base);
  const baseIds = new Set(baseParas.map((p) => p.id).filter(Boolean));

  const sideContent: ChapterBlockNode[] = Array.isArray(sideDoc.content)
    ? sideDoc.content.map((node) => {
        if (!isParagraphBlock(node)) return node;
        return {
          ...node,
          attrs: { ...(node.attrs ?? {}) },
        };
      })
    : [];
  const side: ChapterDocContent = { type: 'doc', content: sideContent };
  const sideParas = listChapterParagraphs(side);
  if (!sideParas.length) return ensureParagraphIds(side);

  const assigned: Array<string | null> = sideParas.map(() => null);
  const claimedBase = new Set<string>();
  const hasOverlap = sideParas.some((p) => Boolean(p.id) && baseIds.has(p.id));

  // Pass 1: keep ids that already exist in base.
  for (let i = 0; i < sideParas.length; i += 1) {
    const id = sideParas[i].id;
    if (!id || !baseIds.has(id) || claimedBase.has(id)) continue;
    assigned[i] = id;
    claimedBase.add(id);
  }

  // Pass 2: exact plain-text match to unclaimed base paragraphs.
  for (let i = 0; i < sideParas.length; i += 1) {
    if (assigned[i]) continue;
    const text = sideParas[i].text;
    const candidates = baseParas.filter((b) => !claimedBase.has(b.id) && b.text === text);
    if (!candidates.length) continue;
    const best =
      candidates.length === 1
        ? candidates[0]
        : candidates.reduce((a, b) => (Math.abs(a.index - i) <= Math.abs(b.index - i) ? a : b));
    assigned[i] = best.id;
    claimedBase.add(best.id);
  }

  // Pass 3: pair remaining side paragraphs with remaining base paragraphs in order
  // (covers in-place edits that no longer share exact text).
  const remainingBase = baseParas.filter((b) => !claimedBase.has(b.id));
  let ri = 0;
  for (let i = 0; i < sideParas.length; i += 1) {
    if (assigned[i]) continue;
    if (ri >= remainingBase.length) break;
    assigned[i] = remainingBase[ri].id;
    claimedBase.add(remainingBase[ri].id);
    ri += 1;
  }

  // Pass 4: true inserts — keep a pre-existing non-base id only when the side
  // already shared the base id namespace (trusted insert). Otherwise mint fresh.
  const usedSideIds = new Set(assigned.filter((id): id is string => Boolean(id)));
  for (let i = 0; i < sideParas.length; i += 1) {
    if (assigned[i]) continue;
    const existing = sideParas[i].id;
    if (
      hasOverlap &&
      existing &&
      !baseIds.has(existing) &&
      !usedSideIds.has(existing)
    ) {
      assigned[i] = existing;
      usedSideIds.add(existing);
    } else {
      const fresh = createParagraphId();
      assigned[i] = fresh;
      usedSideIds.add(fresh);
    }
  }

  for (let i = 0; i < sideParas.length; i += 1) {
    const node = sideParas[i].node;
    node.attrs = { ...(node.attrs ?? {}), id: assigned[i]! };
  }

  return side;
}

export function listChapterParagraphs(doc: ChapterDocContent): ChapterParagraphRef[] {
  const content = Array.isArray(doc.content) ? doc.content : [];
  const refs: ChapterParagraphRef[] = [];
  let index = 0;
  for (const node of content) {
    if (!isParagraphBlock(node)) continue;
    const id = typeof node.attrs?.id === 'string' && node.attrs.id.trim() ? node.attrs.id.trim() : '';
    refs.push({
      index,
      id,
      type: node.type,
      text: blockPlainText(node),
      level: typeof node.attrs?.level === 'number' ? node.attrs.level : undefined,
      node,
    });
    index += 1;
  }
  return refs;
}

/** Alias: blocks with stable ids for diff/merge. */
export const blocksOf = listChapterParagraphs;

export function getParagraphByIndex(doc: ChapterDocContent, paragraphIndex: number): ChapterParagraphRef | null {
  const paragraphs = listChapterParagraphs(doc);
  return paragraphs[paragraphIndex] ?? null;
}

export function getParagraphById(doc: ChapterDocContent, paragraphId: string): ChapterParagraphRef | null {
  const trimmed = paragraphId.trim();
  if (!trimmed) return null;
  return listChapterParagraphs(doc).find((p) => p.id === trimmed) ?? null;
}

export function findParagraphBlockIndexById(doc: ChapterDocContent, paragraphId: string): number | null {
  const ref = getParagraphById(doc, paragraphId);
  return ref ? ref.index : null;
}

export type BlockPatch = {
  /** Target `attrs.id`. */
  blockId: string;
  /** Replacement node; `null` deletes the block. */
  node: ChapterBlockNode | null;
  /**
   * For inserts of unknown ids: place after this block id.
   * If omitted/null and id is new, append at end of tracked blocks.
   */
  afterBlockId?: string | null;
  /** Preferred index among tracked blocks when inserting (0-based). */
  insertIndex?: number | null;
};

/**
 * Apply patches keyed by block id.
 * - Existing id + node → replace
 * - Existing id + null → delete
 * - Unknown id + node → insert (afterBlockId / insertIndex / append)
 */
export function replaceBlocks(doc: ChapterDocContent, patches: BlockPatch[]): ChapterDocContent {
  if (!patches.length) return doc;
  const content = Array.isArray(doc.content) ? [...doc.content] : [];

  const trackedIndices: number[] = [];
  for (let i = 0; i < content.length; i += 1) {
    if (isParagraphBlock(content[i])) trackedIndices.push(i);
  }

  const idToContentIndex = new Map<string, number>();
  for (const ci of trackedIndices) {
    const node = content[ci];
    const id = typeof node.attrs?.id === 'string' ? node.attrs.id.trim() : '';
    if (id) idToContentIndex.set(id, ci);
  }

  const deletes = new Set<string>();
  const replaces = new Map<string, ChapterBlockNode>();
  const inserts: Array<{
    blockId: string;
    node: ChapterBlockNode;
    afterBlockId?: string | null;
    insertIndex?: number | null;
  }> = [];

  for (const patch of patches) {
    const id = typeof patch.blockId === 'string' ? patch.blockId.trim() : '';
    if (!id) continue;
    if (idToContentIndex.has(id)) {
      if (patch.node == null) deletes.add(id);
      else {
        const attrs = { ...(patch.node.attrs ?? {}), id };
        replaces.set(id, { ...patch.node, attrs });
      }
    } else if (patch.node != null) {
      const attrs = { ...(patch.node.attrs ?? {}), id };
      inserts.push({
        blockId: id,
        node: { ...patch.node, attrs },
        afterBlockId: patch.afterBlockId,
        insertIndex: patch.insertIndex,
      });
    }
  }

  const next: ChapterBlockNode[] = [];
  for (let i = 0; i < content.length; i += 1) {
    const node = content[i];
    if (!isParagraphBlock(node)) {
      next.push(node);
      continue;
    }
    const id = typeof node.attrs?.id === 'string' ? node.attrs.id.trim() : '';
    if (id && deletes.has(id)) continue;
    if (id && replaces.has(id)) {
      next.push(replaces.get(id)!);
      continue;
    }
    next.push(node);
  }

  // Rebuild id map on `next` for insert placement
  const nextIdIndex = new Map<string, number>();
  for (let i = 0; i < next.length; i += 1) {
    const node = next[i];
    if (!isParagraphBlock(node)) continue;
    const id = typeof node.attrs?.id === 'string' ? node.attrs.id.trim() : '';
    if (id) nextIdIndex.set(id, i);
  }

  for (const ins of inserts) {
    if (nextIdIndex.has(ins.blockId)) continue;
    let at = next.length;
    if (typeof ins.insertIndex === 'number' && Number.isFinite(ins.insertIndex)) {
      // Map tracked-block index to content index in `next`
      let tracked = 0;
      at = next.length;
      for (let i = 0; i < next.length; i += 1) {
        if (!isParagraphBlock(next[i])) continue;
        if (tracked === Math.max(0, Math.floor(ins.insertIndex))) {
          at = i;
          break;
        }
        tracked += 1;
      }
      if (tracked <= Math.floor(ins.insertIndex!)) at = next.length;
    } else if (ins.afterBlockId && nextIdIndex.has(ins.afterBlockId)) {
      at = (nextIdIndex.get(ins.afterBlockId) ?? -1) + 1;
    }
    next.splice(at, 0, ins.node);
    // Refresh map for subsequent inserts
    nextIdIndex.clear();
    for (let i = 0; i < next.length; i += 1) {
      const node = next[i];
      if (!isParagraphBlock(node)) continue;
      const id = typeof node.attrs?.id === 'string' ? node.attrs.id.trim() : '';
      if (id) nextIdIndex.set(id, i);
    }
  }

  return { type: 'doc', content: next };
}

/** Count how many tracked blocks are missing ids (for backfill dry-run). */
export function countMissingParagraphIds(doc: ChapterDocContent): number {
  const content = Array.isArray(doc.content) ? doc.content : [];
  let missing = 0;
  for (const node of content) {
    if (!isParagraphBlock(node)) continue;
    const id = node.attrs?.id;
    if (typeof id !== 'string' || !id.trim()) missing += 1;
  }
  return missing;
}
